import React, { useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext.js";
import ErrorBoundary from "./components/ErrorBoundary.js";
import Login from "./components/Login.js";
import Schedule from "./components/Schedule.js";
import StudentForm from "./components/StudentForm.js";
import StudentNew from "./components/StudentNew.js";
import CoachNew from "./components/CoachNew.js";
import CoachForm from "./components/CoachForm.js";
import SavedSchedules from "./components/SavedSchedules.js";
import Export from "./components/Export.js";
import NotAssignedStudents from "./components/NotAssignedStudents.js";
import ImportRegistrations from "./components/ImportRegistrations.js";
import Settings from "./components/Settings.js";
import Users from "./components/Users.js";
import LoginActivity from "./components/LoginActivity.js";
import CoachDashboard from "./components/CoachDashboard.js";
import StudentDashboard from "./components/StudentDashboard.js";
import ErrorTester from "./components/ErrorTester.js";

const MainApp = () => {
  const { user, logout, isAdmin } = useAuth();

  // Set initial view based on user role
  const getInitialView = () => {
    if (user.role === "coach") return "coachDashboard";
    if (user.role === "student") return "studentDashboard";
    return "schedule"; // admin default
  };

  const [view, setView] = useState(getInitialView());
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showLogoutDropdown, setShowLogoutDropdown] = useState(false);

  const renderView = () => {
    switch (view) {
      case "schedule":
        return <Schedule onNavigate={setView} />;
      case "students":
        return <StudentForm />;
      case "StudentNew":
        return <StudentNew />;
      case "CoachNew":
        return <CoachNew />;
      case "CoachForm":
        return <CoachForm />;
      case "savedSchedules":
        return <SavedSchedules onNavigate={setView} />;
      case "export":
        return <Export />;
      case "notAssigned":
        return <NotAssignedStudents />;
      case "importRegistrations":
        return <ImportRegistrations />;
      case "settings":
        return <Settings onNavigate={setView} />;
      case "userManagement":
        return isAdmin ? (
          <Users />
        ) : (
          <div>Keine Berechtigung</div>
        );
      case "loginActivity":
        return isAdmin ? (
          <LoginActivity />
        ) : (
          <div>Keine Berechtigung</div>
        );
      case "coachDashboard":
        return user.role === "coach" ? (
          <CoachDashboard />
        ) : (
          <div>Keine Berechtigung</div>
        );
      case "studentDashboard":
        return user.role === "student" ? (
          <StudentDashboard />
        ) : (
          <div>Keine Berechtigung</div>
        );
      case "errorTester":
        return process.env.NODE_ENV === "development" && isAdmin ? (
          <ErrorTester />
        ) : (
          <div>Nur in Entwicklungsumgebung verfügbar</div>
        );
      default:
        return null;
    }
  };

  return (
    <ErrorBoundary>
      <DndProvider backend={HTML5Backend}>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        <div
          style={{
            display: "flex",
            height: "100vh",
            margin: 0,
          }}
        >
          {/* Sidebar */}
          <div
            style={{
              width: sidebarOpen ? 200 : 50,
              background: "#222",
              color: "#fff",
              transition: "width 0.3s ease",
              overflow: "auto",
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* User info section */}
            {sidebarOpen && user && (
            <div
              style={{
                padding: "1rem",
                borderBottom: "1px solid #444",
                fontSize: "0.875rem",
                position: "relative",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={() => setShowLogoutDropdown(true)}
              onMouseLeave={() => setShowLogoutDropdown(false)}
            >
              <div style={{ fontWeight: "bold", marginBottom: "0.25rem" }}>
                {user.firstName} {user.lastName}
              </div>
              <div style={{ color: "#aaa", fontSize: "0.75rem" }}>
                {user.role === "admin" && "🛡️ Administrator"}
                {user.role === "coach" && "🎾 Trainer"}
                {user.role === "student" && "👨‍🎓 Schüler"}
              </div>

              {/* Logout dropdown */}
              {showLogoutDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    background: "#333",
                    borderTop: "1px solid #555",
                    zIndex: 1000,
                  }}
                >
                  <button
                    onClick={logout}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.75rem 1rem",
                      background: "#dc2626",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#b91c1c"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "#dc2626"}
                  >
                    🚪 Abmelden
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            onClick={() => setSidebarOpen((o) => !o)}
            style={{
              width: "100%",
              padding: "0.5rem",
              background: "none",
              border: "none",
              color: "white",
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            {sidebarOpen ? "←" : "→"}
          </button>

          {/* nur anzeigen, wenn Sidebar geöffnet */}
          {sidebarOpen && (
            <nav style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {/* Coach menu */}
              {user.role === "coach" && (
                <>
                  <button
                    onClick={() => setView("coachDashboard")}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.5rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    🏠 Dashboard
                  </button>
                  <button
                    onClick={() => setView("schedule")}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.5rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    📅 Trainingsplan
                  </button>
                </>
              )}

              {/* Student menu */}
              {user.role === "student" && (
                <button
                  onClick={() => setView("studentDashboard")}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "0.5rem",
                    background: "none",
                    border: "none",
                    color: "white",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  🏠 Mein Dashboard
                </button>
              )}

              {/* Admin menu */}
              {user.role === "admin" && (
                <>
                  <button
                    onClick={() => setView("schedule")}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.5rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    📅 Trainingsplan
                  </button>
                  <button
                    onClick={() => setView("students")}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.5rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    👥 Schüler verwalten
                  </button>
                  <button
                    onClick={() => setView("StudentNew")}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.5rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    ➕ Schüler anlegen
                  </button>
                  <button
                    onClick={() => setView("CoachNew")}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.5rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    🎾 Trainer anlegen
                  </button>
                  <button
                    onClick={() => setView("CoachForm")}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.5rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    👨‍🏫 Trainer verwalten
                  </button>
                  <button
                    onClick={() => setView("savedSchedules")}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.5rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    💾 Gespeicherte Trainigspläne
                  </button>
                  <button
                    onClick={() => setView("export")}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.5rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    📤 Import/Export
                  </button>
                  <button
                    onClick={() => setView("notAssigned")}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.5rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    ❓ Nicht zugewiesene Schüler
                  </button>
                  <button
                    onClick={() => setView("importRegistrations")}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.5rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    📧 Registrierungen importieren
                  </button>
                  <button
                    onClick={() => setView("settings")}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.5rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    ⚙️ Einstellungen
                  </button>

                  {/* Development only: Error Tester */}
                  {process.env.NODE_ENV === "development" && (
                    <button
                      onClick={() => setView("errorTester")}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "0.5rem",
                        background: "#dc2626",
                        border: "none",
                        color: "white",
                        textAlign: "left",
                        cursor: "pointer",
                        marginTop: "1rem",
                        borderRadius: "4px",
                      }}
                    >
                      🧪 Error Tester (DEV)
                    </button>
                  )}
                </>
              )}
            </nav>
          )}
        </div>

        {/* Hauptinhalt */}
        <main
          style={{
            flexGrow: 1,
            overflowY: "auto",
            padding: "1rem",
            background: "#f0f4f8",
            boxSizing: "border-box",
            height: "100%",
          }}
        >
          {renderView()}
        </main>
      </div>
    </DndProvider>
    </ErrorBoundary>
  );
};

// Wrapper component to handle authentication
const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "1.5rem",
        }}
      >
        Lädt...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return <MainApp />;
};

export default App;
